import clsx from 'clsx';
import { Fragment } from 'react';
import { SimilarProducts } from '../../../container/products/similarProducts';
import { IProductVariant } from '../../../services/helper/productInterfaces';
import {
  IMarketingBanners,
  IMarketingGridItem,
  IMarketingGridProps,
  IMarketingTiles,
  MARKETINGCONTENTTYPE,
} from './IMarketingGrid';
import MarketingContent from './marketing-content';
import MarketingTile from './marketing-tile';
import ArticleBanner from '../banner/articleBanner';
import { DataAnalyticsLocations, buildAnalytics } from '../../basic/analytics';
import { getRegionFromPath } from '../../../utils/regionUtils';
import { useDevice } from '../../../providers/device-provider';
import { usePersonalizeStore } from '../../../stores/personalizeStore';
import { ProductCategoryKeys } from '../../../common/enums/common';
import { buildUrlWithParams } from '../../../common/functions/buildProductPdpUrl';
import useSplunkLogger from '../../../common/hooks/useSplunkLogger';
import { navigate } from '../../../common/router/history';

interface IMarketingGridComponent extends IMarketingGridItem {
  value: any;
}

const MarketingGrid = ({ marketingProps }: IMarketingGridProps): JSX.Element => {
  const { isMobile } = useDevice();
  const styles = `flex flex-col lg:flex-row lg:space-y-0 lg:space-x-md lg:items-end lg:justify-between
  xl:flex-row xl:mb-xl space-y-lg xl:space-y-0 xl:space-x-md xl:items-end xl:justify-between ${isMobile ? 'mx-md' : ''}`;

  const gridAnalyticsLocation: DataAnalyticsLocations = DataAnalyticsLocations.MarketingGrid;

  // TODO: Add any other marketing content to the Marketing Grid
  const components: Array<IMarketingGridComponent> = marketingProps?.map((marketingItem, index) => {
    const items = (marketingItem.value as any) || [];
    switch (marketingItem.type) {
      case MARKETINGCONTENTTYPE.BANNERS:
        return {
          ...marketingItem,
          value: (
            <MarketingBanners
              banners={marketingItem.value}
              parentAnalyticsLocation={gridAnalyticsLocation}
              key={index}
            />
          ),
        };
      case MARKETINGCONTENTTYPE.TILES:
        return {
          ...marketingItem,
          value: (
            <MarketingTiles
              tiles={marketingItem.value}
              parentAnalyticsLocation={gridAnalyticsLocation}
              className={styles}
              key={index}
            />
          ),
        };
      case MARKETINGCONTENTTYPE.CAROUSEL:
        return {
          ...marketingItem,
          value: items.map(({ type, title }: any) => {
            return (
              <MarketingCarousel
                parentAnalyticsLocation={gridAnalyticsLocation}
                key={index}
                type={type}
                title={title}
              />
            );
          }),
        };
      default:
        return {
          ...marketingItem,
          value: <Fragment key={index}></Fragment>,
        };
    }
  });

  return components ? (
    <div
      className={clsx('xl:mx-0 mt-lg my-lg')}
      data-testid="marketing-grid"
      aria-label={components?.length > 0 ? "marketing grid section" : ""}
    >
      {components?.map((_x, index) => {
        return components.find(y => y.position === index)?.value;
      })}
    </div>
  ) : (
    <></>
  );
};

const MarketingCarousel = ({ type, title, parentAnalyticsLocation }: any): JSX.Element => {
  const { splunkLogger } = useSplunkLogger()
  const setProductPersonalization = usePersonalizeStore(state => state.setProductPersonalization);
  const onProductTileClick = (productVariant: IProductVariant, prodId: string) => {
    const url = buildUrlWithParams("/glasses/frames/pdp", { id: prodId, variantId: productVariant?.id || 1, color: productVariant?.color, eyesize: productVariant?.eyeSize }, splunkLogger);
    if (window) {
      const origin = window.location.origin;
      const region = getRegionFromPath();
      setProductPersonalization({
        brand: productVariant?.brand,
        gender: productVariant?.gender,
        shape: productVariant?.shape,
        material: productVariant?.material,
        type: productVariant?.type,
        color: productVariant?.color,
        productCategory: productVariant?.categories?.[0].key as ProductCategoryKeys,
        price: productVariant?.price?.toString(),
        size: productVariant?.frameSize?.toString(),
      });
      const redirectUrl = `${origin}/${region}${url}`;
      navigate(redirectUrl);
    }
  };
  return (
    <SimilarProducts
      carouselId={type}
      title={title}
      onProductTileClick={onProductTileClick}
      prodType={type}
    />
  );
};

const MarketingBanners = ({ banners, parentAnalyticsLocation }: IMarketingBanners): JSX.Element => {
  return (
    <>
      {banners?.map((banner: any, idx: number) => {
        return banner.banner_style === 'with_floating_card' ? (
          <ArticleBanner
            banner={banner}
            card={banner.card}
            bannerStyle={banner.banner_style}
            parentAnalyticsLocation={parentAnalyticsLocation}
          />
        ) : (
          <MarketingContent
            banner={banner}
            key={idx}
            parentAnalyticsLocation={parentAnalyticsLocation}
          />
        );
      })}
    </>
  );
};

const MarketingTiles = ({ tiles, className, parentAnalyticsLocation }: IMarketingTiles) => {
  return (
    <div className={className}>
      {tiles?.map((banner: any, idx: number) => {
        return (
          <MarketingTile
            key={idx}
            tileIndex={idx}
            banner={banner}
            parentAnalyticsLocation={parentAnalyticsLocation}
          />
        );
      })}
    </div>
  );
};

export default MarketingGrid;
